import { supabase } from '@/lib/supabase';
import { SignalAccountStatus, PaginatedResponse } from '@/types/signal';

export interface CTraderSignalFilters {
  status?: 'OPEN' | 'PROCESS' | 'REJECTED' | 'all';
  dateFrom?: string;
  dateTo?: string;
  symbol?: string;
  accountId?: string;
}

export const fetchCTraderSignalStatuses = async (
  page: number = 1,
  pageSize: number = 10,
  filters: CTraderSignalFilters = {}
): Promise<PaginatedResponse<SignalAccountStatus>> => {
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  let query = supabase
    .from('signal_account_status')
    .select('*', { count: 'exact' })
    .order('response_time', { ascending: false })
    .range(start, end);

  // Apply filters
  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters.dateFrom) {
    query = query.gte('response_time', filters.dateFrom);
  }

  if (filters.dateTo) {
    query = query.lte('response_time', filters.dateTo);
  }

  if (filters.symbol) {
    query = query.ilike('symbol', `%${filters.symbol}%`);
  }

  if (filters.accountId) {
    query = query.ilike('accountid_trading', `%${filters.accountId}%`);
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Error fetching cTrader signals: ${error.message}`);
  }

  return {
    data: data || [],
    total: count || 0,
    page,
    pageSize
  };
};

export const fetchCTraderSignalStatusById = async (id: string): Promise<SignalAccountStatus> => {
  const { data, error } = await supabase
    .from('signal_account_status')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Error fetching signal status: ${error.message}`);
  }

  return data;
};

export const getCTraderSignalStats = async () => {
  // Get total count
  const { count: total, error: totalError } = await supabase
    .from('signal_account_status')
    .select('*', { count: 'exact', head: true });

  if (totalError) {
    throw new Error(`Error fetching total signal stats: ${totalError.message}`);
  }

  // Get count by status
  const [openResult, processResult, rejectedResult] = await Promise.all([
    supabase
      .from('signal_account_status')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'OPEN'),
    supabase
      .from('signal_account_status')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'PROCESS'),
    supabase
      .from('signal_account_status')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'REJECTED')
  ]);

  if (openResult.error || processResult.error || rejectedResult.error) {
    throw new Error('Error fetching signal stats by status');
  }

  const stats = {
    total: total || 0,
    open: openResult.count || 0,
    process: processResult.count || 0,
    rejected: rejectedResult.count || 0,
  };

  return stats;
};